import React, { useState } from 'react';
import { ArrowCircleDown } from '@mui/icons-material';
import {
  ButtonContainer,
  Loading,
  Alert,
  type AlertDetails,
  useFilter,
  Button,
  COMMON_PAGE_TEXT,
} from '../../../../components';
import {
  formatFileNameDateString,
  handlePageLoading,
  type PostRequestData,
  postRequest,
} from '../../../../utils';
import {
  ApplicationViewTableFilter,
  type ApplicationViewTableFilterProps,
} from './ApplicationViewTableFilter';
import {
  ApplicationViewTable,
  type ApplicationViewTableProps,
} from './ApplicationViewTable';
import {
  ApplicationViewQuickFilters,
  type ApplicationViewQuickFiltersProps,
} from './ApplicationViewQuickFilter';
import {
  getTableQueryFilter,
  getTableFilter,
  getQueryFilter,
  getDefaultQuickFilter,
} from './applicationViewTableFunctions';
import type { TickActions, QuickFilter } from './types';

// TODO make ApplicationViewTools component with application profile download function
export type ToolsProps = {
  enableExport?: boolean;
  tickActions?: TickActions[];
  customTools?: React.ReactNode[];
};

export type ApplicationViewListProps = {
  toolsProps?: ToolsProps;
  quickFilterProps?: Pick<ApplicationViewQuickFiltersProps, 'quickFilters'>;
  tableProps?: Omit<ApplicationViewTableProps, 'quickFilter' | 'tableFilter'>;
  filterProps?: ApplicationViewTableFilterProps;
};

export const ApplicationViewList: React.FunctionComponent<
  ApplicationViewListProps
> = (props: ApplicationViewListProps) => {
  const { tableProps, filterProps, toolsProps = {}, quickFilterProps } = props;
  const { enableExport = true, tickActions = [], customTools } = toolsProps;

  const listFilter = tableProps?.listFilter;
  const defaultQuickFilters = getDefaultQuickFilter(
    quickFilterProps?.quickFilters,
  )?.filter;

  const { filter } = useFilter();
  const [quickFilter, setQuickFilter] = useState(defaultQuickFilters);
  const filterKeyMapping = getTableFilter();
  const tableFilter = getTableQueryFilter(filter, filterKeyMapping);

  const [pageLoading, setPageLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'info',
  });

  const handleAppliedQuickFilterChange = (
    quickFilter: QuickFilter['filter'],
  ): void => {
    setQuickFilter(quickFilter);
  };

  const handleApplicationExport = async (): Promise<void> => {
    const queryFilter = getQueryFilter(tableFilter, listFilter, quickFilter);
    try {
      // pass filter json as post body
      const postData: PostRequestData = {
        url: `/eadmin-operation/general/export/applicationView`,
        data: queryFilter,
        contentType: 'application/json',
      };
      const response = await postRequest(postData);

      const blob = new Blob([response.data], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      const current = new Date();
      const formattedDateTime = formatFileNameDateString(current);
      link.setAttribute(
        'download',
        `GS-applications-data-${formattedDateTime}.csv`,
      );

      // Trigger the download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Revoke the URL to free up memory
      URL.revokeObjectURL(url);
    } catch (error) {
      setAlertDetails({
        alertContent: 'Error Occurs.' + (error as Error)?.message,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
  };

  const handleExport = async (): Promise<void> => {
    await handlePageLoading(setPageLoading, handleApplicationExport);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
        width: '100%',
        gap: '4px',
      }}
    >
      <Loading openLoading={pageLoading} />
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />

      <ApplicationViewTableFilter {...filterProps} />

      <div className="flex w-full">
        {customTools}

        <ApplicationViewQuickFilters
          {...quickFilterProps}
          onAppliedQuickFilterChange={handleAppliedQuickFilterChange}
        />
        <ButtonContainer
          width={'100%'}
          display={'flex'}
          justifyContent={'flex-end'}
          spacing={1}
        >
          {enableExport && (
            <Button
              onClick={handleExport}
              startIcon={<ArrowCircleDown />}
              variant="text"
              sx={{
                textTransform: 'none',
                minWidth: '200px',
              }}
            >
              {COMMON_PAGE_TEXT.BUTTON.EXPORT_APPLICATION_DATA}
            </Button>
          )}
        </ButtonContainer>
      </div>

      <ApplicationViewTable
        {...tableProps}
        quickFilter={quickFilter}
        tableFilter={tableFilter}
        tickActions={tickActions}
      />
    </div>
  );
};
